/**
 * Kit Builder — manages kit component selection, price tallying, required validation,
 * and cart property injection for product kits.
 */

class KitBuilder {
  constructor(root) {
    this.root = root;
    this.cards = Array.from(root.querySelectorAll('[data-kit-item]'));
    if (!this.cards.length) return;

    this.productFormId = root.dataset.productFormId;
    this.form = this.productFormId ? document.getElementById(this.productFormId) : null;
    this.requiredButtonText = root.dataset.requiredButtonText || '';

    const submitSelector = this.productFormId
      ? `.product-form__submit[form="${this.productFormId}"]`
      : '.product-form__submit';
    this.submitButton = this.form?.querySelector(submitSelector)
      || document.querySelector(submitSelector)
      || this.form?.querySelector('[type="submit"]');
    this.submitLabel = this.submitButton?.querySelector('.btn-text') || null;
    this.originalSubmitText = this.submitLabel?.textContent?.trim() || '';

    this.errorMessage = this.form?.querySelector('.product-form__error-message') || null;

    this.productInfo = root.closest('product-info') || null;
    // Find the Add to Cart button's price element (product-buy-price custom element)
    // The button and price element may be outside the form, connected via form attribute
    const formId = this.productFormId;
    this.buyPriceEl = formId
      ? document.querySelector(`product-buy-price[form="${formId}"]`)
      : null;
    if (!this.buyPriceEl) {
      this.buyPriceEl = (this.productInfo || document).querySelector('product-buy-price');
    }
    this.originalBuyPriceValue = this.buyPriceEl?.dataset.price ? parseInt(this.buyPriceEl.dataset.price, 10) : 0;

    this.groups = Array.from(root.querySelectorAll('[data-kit-group]'));

    this.conditionalBlocks = Array.from(
      (this.productInfo || document).querySelectorAll('[data-required-variant]')
    );

    // Watch for main variant price changes
    if (this.buyPriceEl) {
      this.priceObserver = new MutationObserver(() => this.updateTotalPrice());
      this.priceObserver.observe(this.buyPriceEl, { attributes: true, attributeFilter: ['data-price'] });
    }

    this.init();
  }

  init() {
    this.cards.forEach(card => this.setupCard(card));
    this.applyPresetOptions();
    this.bindEvents();
    this.attachFormGuard();
    this.updateTotalPrice();
    this.updateSubmitState();

    // Listen for quantity changes
    const qtyInput = (this.productInfo || document).querySelector('quantity-input input[name="quantity"]');
    if (qtyInput) {
      qtyInput.addEventListener('change', () => this.updateTotalPrice());
      // Also listen on the quantity-input element for button clicks
      const qtyContainer = qtyInput.closest('quantity-input');
      if (qtyContainer) {
        qtyContainer.addEventListener('click', () => {
          setTimeout(() => this.updateTotalPrice(), 50);
        });
      }
    }

    // Listen for main product variant changes (conditional visibility)
    const mainForm = this.form || document.querySelector('form[action$="/cart/add"]');
    if (mainForm) {
      mainForm.addEventListener('variant:change', () => {
        setTimeout(() => {
          this.updateTotalPrice();
          this.updateConditionalVisibility();
        }, 50);
      });
    }
  }

  setupCard(card) {
    const hiddenInput = card.querySelector('.kit-component__input');
    const variantsScript = card.querySelector('[data-product-variants]');

    if (variantsScript) {
      try {
        card._productVariants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        card._productVariants = [];
      }
    }

    // Apply variant name mapping
    const nameMapStr = card.dataset.variantNameMap;
    if (nameMapStr && nameMapStr !== '{}') {
      try {
        const nameMap = JSON.parse(nameMapStr);
        this.applyVariantNameMap(card, nameMap);
      } catch (e) {
        // ignore malformed JSON
      }
    }

    // For required items, auto-check
    if (card.dataset.required === 'true' && hiddenInput && !hiddenInput.checked) {
      hiddenInput.checked = true;
    }

    // Store card reference on the modal so we can find it after it moves to body
    const modal = card.querySelector('x-modal');
    if (modal) {
      modal._kitCard = card;
    }
  }

  applyVariantNameMap(card, nameMap) {
    if (!nameMap || Object.keys(nameMap).length === 0) return;

    // Find all variant picker labels within this card
    const labels = card.querySelectorAll(
      '.label-swatch span, .swatch-label, variant-picker label span, .variant-label, .product-form__input label'
    );
    labels.forEach(label => {
      const original = label.textContent.trim();
      if (nameMap[original]) {
        label.textContent = nameMap[original];
      }
    });

    // Also update dropdown options
    const selects = card.querySelectorAll('variant-picker select option');
    selects.forEach(option => {
      const original = option.textContent.trim();
      if (nameMap[original]) {
        option.textContent = nameMap[original];
      }
    });
  }

  applyPresetOptions() {
    this.cards.forEach(card => {
      const presetAttr = card.dataset.presetOptions;
      if (!presetAttr) return;

      let presets;
      try { presets = JSON.parse(presetAttr); } catch(e) { return; }

      const picker = card.querySelector('[data-kit-picker]');
      if (!picker) return;

      for (const [slug, value] of Object.entries(presets)) {
        const fieldset = picker.querySelector(`[data-option-slug="${slug}"]`);
        if (!fieldset) continue;

        // Try radio inputs (button/swatch pickers)
        const radios = fieldset.querySelectorAll('input[type="radio"]');
        let found = false;
        radios.forEach(radio => {
          const radioVal = radio.dataset.optionValue || radio.value;
          if (radioVal === value) {
            radio.checked = true;
            found = true;
          }
        });

        // Try select (dropdown picker)
        if (!found) {
          const select = fieldset.querySelector('select');
          if (select) {
            select.value = value;
            found = true;
          }
        }

        // Hide the fieldset
        if (found) {
          fieldset.style.display = 'none';
        }
      }

      // Filter the initial server-rendered variant title
      const titleEl = card.querySelector('[data-kit-variant-title]');
      if (titleEl && titleEl.textContent) {
        const presetValues = new Set(Object.values(presets));
        titleEl.textContent = titleEl.textContent.split(' / ').filter(part => !presetValues.has(part.trim())).join(' / ');
      }

      // After setting presets, resolve the correct variant
      const variant = this.getSelectedVariant(card);
      if (variant) {
        this.updateCardVariant(card, variant);
      }
    });
  }

  bindEvents() {
    // Listen for select button clicks
    this.root.addEventListener('click', (event) => {
      // Grid card click — entire card is the toggle target
      const cardToggle = event.target.closest('[data-kit-card-toggle]');
      if (cardToggle && !event.target.closest('[data-kit-toggle]')) {
        const card = cardToggle.closest('[data-kit-item]');
        if (!card) return;
        const toggleBtn = card.querySelector('[data-kit-toggle]');
        if (!toggleBtn) return;
        const isPressed = toggleBtn.getAttribute('aria-pressed') === 'true';
        const newState = !isPressed;

        const group = card.closest('[data-kit-group]');
        if (group && group.dataset.selectionMode === 'one_required' && newState) {
          group.querySelectorAll('[data-kit-item]').forEach(sibling => {
            if (sibling === card) return;
            const siblingToggle = sibling.querySelector('[data-kit-toggle]');
            if (siblingToggle && siblingToggle.getAttribute('aria-pressed') === 'true') {
              siblingToggle.setAttribute('aria-pressed', 'false');
              this.updateGroupBtnText(siblingToggle, false);
              this.toggleOptional(sibling, false);
            }
          });
        }

        toggleBtn.setAttribute('aria-pressed', newState);
        this.updateGroupBtnText(toggleBtn, newState);
        this.toggleOptional(card, newState);
        return;
      }

      const toggle = event.target.closest('[data-kit-toggle]');
      if (toggle) {
        const card = toggle.closest('[data-kit-item]');
        if (!card) return;
        const toggleBtn = card.querySelector('[data-kit-toggle]');
        if (!toggleBtn) return; // required items have no toggle
        const isPressed = toggleBtn.getAttribute('aria-pressed') === 'true';
        const newState = !isPressed;

        // Group mutual exclusion: "one_required" mode deselects siblings
        const group = card.closest('[data-kit-group]');
        if (group && group.dataset.selectionMode === 'one_required' && newState) {
          group.querySelectorAll('[data-kit-item]').forEach(sibling => {
            if (sibling === card) return;
            const siblingToggle = sibling.querySelector('[data-kit-toggle]');
            if (siblingToggle && siblingToggle.getAttribute('aria-pressed') === 'true') {
              siblingToggle.setAttribute('aria-pressed', 'false');
              this.updateGroupBtnText(siblingToggle, false);
              this.toggleOptional(sibling, false);
            }
          });
        }

        toggleBtn.setAttribute('aria-pressed', newState);
        this.updateGroupBtnText(toggleBtn, newState);
        this.toggleOptional(card, newState);
      }

      // Customer qty stepper buttons
      const minusBtn = event.target.closest('[data-kit-qty-minus]');
      const plusBtn = event.target.closest('[data-kit-qty-plus]');
      if (minusBtn || plusBtn) {
        const card = event.target.closest('[data-kit-item]');
        if (!card) return;
        const qtyInput = card.querySelector('[data-kit-qty-input]');
        if (!qtyInput) return;
        const min = parseInt(qtyInput.min || '1', 10);
        const max = parseInt(qtyInput.max || '0', 10);
        let val = parseInt(qtyInput.value || '1', 10);
        if (minusBtn) val = Math.max(min, val - 1);
        if (plusBtn) val = max > 0 ? Math.min(max, val + 1) : val + 1;
        qtyInput.value = val;
        this.updateComponentQty(card, val);
      }
    });

    // Listen for variant picker changes within kit cards.
    // We listen on document because x-modal moves to document.body when opened,
    // so change events from the picker no longer bubble to this.root.
    document.addEventListener('change', (event) => {
      // Customer qty input direct edit
      const qtyInput = event.target.closest('[data-kit-qty-input]');
      if (qtyInput) {
        const card = this._findKitCard(qtyInput);
        if (!card) return;
        const min = parseInt(qtyInput.min || '1', 10);
        const max = parseInt(qtyInput.max || '0', 10);
        let val = parseInt(qtyInput.value || '1', 10);
        if (isNaN(val) || val < min) val = min;
        if (max > 0 && val > max) val = max;
        qtyInput.value = val;
        this.updateComponentQty(card, val);
        return;
      }

      const pickerInput = event.target.closest('variant-picker input[type="radio"], variant-picker select');
      if (pickerInput) {
        const card = this._findKitCard(pickerInput);
        if (!card) return;

        const variant = this.getSelectedVariant(card);
        if (variant) {
          this.updateCardVariant(card, variant);
        }
        return;
      }

    });

    // Drawer "Select" button — selects the optional card and closes the drawer
    document.addEventListener('click', (event) => {
      const selectBtn = event.target.closest('[data-kit-drawer-select]');
      if (!selectBtn) return;
      const card = this._findKitCard(selectBtn);
      if (!card) return;

      // Toggle selection on
      if (!card.classList.contains('kit-component--selected')) {
        const toggle = card.querySelector('[data-kit-toggle]');
        if (toggle) toggle.click();
      }

      // Update button text to "Selected"
      selectBtn.querySelector('.btn-text').textContent = 'Selected';
      selectBtn.disabled = true;

      // Close the drawer
      const trigger = card.querySelector('[data-kit-picker-trigger]');
      if (trigger) {
        setTimeout(() => trigger.click(), 100);
      }
    });
  }

  /**
   * Find the kit card for an element — works even when the element
   * is inside an x-modal that has been moved to document.body.
   */
  _findKitCard(el) {
    // Direct ancestor lookup (element still inside the card)
    const card = el.closest('[data-kit-item]');
    if (card && this.cards.includes(card)) return card;

    // Element is inside a modal that was moved to body
    const modal = el.closest('x-modal');
    if (modal && modal._kitCard && this.cards.includes(modal._kitCard)) {
      return modal._kitCard;
    }
    return null;
  }

  getSelectedVariant(card) {
    const variants = card._productVariants || [];
    if (!variants.length) return null;

    // The picker may be inside a modal that's been moved to document.body
    let picker = card.querySelector('[data-kit-picker]');
    if (!picker) {
      // Look for the modal that references this card
      const modals = document.querySelectorAll('x-modal');
      for (const modal of modals) {
        if (modal._kitCard === card) {
          picker = modal.querySelector('[data-kit-picker]');
          break;
        }
      }
    }
    if (!picker) return variants[0];

    // Collect selected option values
    const selectedValues = [];
    const radios = picker.querySelectorAll('variant-picker input[type="radio"]:checked');
    const selects = picker.querySelectorAll('variant-picker select');

    radios.forEach(radio => {
      const val = radio.dataset.optionValue || radio.value;
      if (val) selectedValues.push(val);
    });
    selects.forEach(select => {
      if (select.value) selectedValues.push(select.value);
    });

    if (!selectedValues.length) return variants[0];

    // Find matching variant
    for (const variant of variants) {
      const opts = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      if (selectedValues.length === opts.length && selectedValues.every((val, i) => val === opts[i])) {
        return variant;
      }
    }

    return null;
  }

  updateCardVariant(card, variant) {
    const hiddenInput = card.querySelector('.kit-component__input');
    if (hiddenInput) {
      hiddenInput.value = variant.id;
      hiddenInput.dataset.price = variant.price;
      hiddenInput.dataset.comparePrice = variant.compare_at_price || '';
      hiddenInput.disabled = !variant.available;
    }

    // Update displayed variant title (filter out preset option values)
    const titleEl = card.querySelector('[data-kit-variant-title]');
    if (titleEl) {
      let displayTitle = variant.title === 'Default Title' ? '' : variant.title;
      const presetAttr = card.dataset.presetOptions;
      if (displayTitle && presetAttr) {
        try {
          const presets = JSON.parse(presetAttr);
          const presetValues = new Set(Object.values(presets));
          displayTitle = displayTitle.split(' / ').filter(part => !presetValues.has(part.trim())).join(' / ');
        } catch(e) {}
      }
      titleEl.textContent = displayTitle;
    }

    // Update price display (multiply by component qty × multiplier)
    const priceEl = card.querySelector('[data-kit-item-price]');
    if (priceEl) {
      const input = card.querySelector('input[name="kit_items"]');
      const baseQty = parseInt(input?.dataset.qty || '1', 10);
      const multiplier = parseInt(input?.dataset.qtyMultiplier || '1', 10) || 1;
      const qty = baseQty * multiplier;
      if (!variant.available) {
        priceEl.innerHTML = '<span class="text-opacity">' + (window.theme?.variantStrings?.soldOut || 'Sold out') + '</span>';
      } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceEl.innerHTML =
          '<s class="kit-component__price-compare text-opacity">' + this.formatMoney(variant.compare_at_price * qty) + '</s> ' +
          '<span class="kit-component__price-sale font-medium">' + this.formatMoney(variant.price * qty) + '</span>' +
          (qty > 1 ? ' <span class="text-opacity text-xs">(' + qty + ' × ' + this.formatMoney(variant.price) + ')</span>' : '');
      } else {
        priceEl.innerHTML = '<span class="font-medium">' + this.formatMoney(variant.price * qty) + '</span>' +
          (qty > 1 ? ' <span class="text-opacity text-xs">(' + qty + ' × ' + this.formatMoney(variant.price) + ')</span>' : '');
      }
    }

    // Update image (list mode)
    const imgEl = card.querySelector('.kit-component__img');
    if (imgEl && variant.featured_image) {
      const baseSrc = variant.featured_image.src.replace(/^https?:/, '');
      const src200 = baseSrc.replace(/(\.[^.]+)$/, '_200x$1');
      const src100 = baseSrc.replace(/(\.[^.]+)$/, '_100x$1');
      imgEl.setAttribute('src', src200);
      imgEl.setAttribute('srcset', src100 + ' 100w, ' + src200 + ' 200w');
      imgEl.setAttribute('alt', variant.featured_image.alt || '');
    }

    // Update image (grid mode)
    const gridImgEl = card.querySelector('.kit-component__card-img');
    if (gridImgEl && variant.featured_image) {
      const baseSrc = variant.featured_image.src.replace(/^https?:/, '');
      const src400 = baseSrc.replace(/(\.[^.]+)$/, '_400x$1');
      const src600 = baseSrc.replace(/(\.[^.]+)$/, '_600x$1');
      gridImgEl.setAttribute('src', src400);
      gridImgEl.setAttribute('srcset', src400 + ' 400w, ' + src600 + ' 600w');
      gridImgEl.setAttribute('alt', variant.featured_image.alt || '');
    }

    // Update image (picker drawer) — fall back to product featured image
    // The modal may have been moved to document.body, so check both locations
    let drawerImgContainer = card.querySelector('[data-kit-drawer-img]');
    if (!drawerImgContainer) {
      const modals = document.querySelectorAll('x-modal');
      for (const modal of modals) {
        if (modal._kitCard === card) {
          drawerImgContainer = modal.querySelector('[data-kit-drawer-img]');
          break;
        }
      }
    }
    if (drawerImgContainer) {
      const imgSource = variant.featured_image || (card.dataset.productFeaturedImage ? { src: card.dataset.productFeaturedImage, alt: '' } : null);
      if (imgSource) {
        const drawerImg = drawerImgContainer.querySelector('.kit-picker-drawer__img');
        if (drawerImg) {
          const baseSrc = imgSource.src.replace(/^https?:/, '');
          const src400 = baseSrc.replace(/(\.[^.]+)$/, '_400x$1');
          const src600 = baseSrc.replace(/(\.[^.]+)$/, '_600x$1');
          drawerImg.setAttribute('src', src600);
          drawerImg.setAttribute('srcset', src400 + ' 400w, ' + src600 + ' 600w');
          drawerImg.setAttribute('alt', imgSource.alt || '');
        }
      }
    }

    this.updateTotalPrice();
    this.updateSubmitState();
  }

  updateComponentQty(card, qty) {
    const hiddenInput = card.querySelector('.kit-component__input');
    if (hiddenInput) {
      hiddenInput.dataset.qty = qty;
    }

    // Update multiplier total text
    const multiplier = parseInt(hiddenInput?.dataset.qtyMultiplier || '1', 10) || 1;
    const totalEl = card.querySelector('[data-kit-qty-total]');
    if (totalEl && multiplier > 1) {
      totalEl.textContent = (qty * multiplier) + ' total added to cart';
    }

    // Re-render the price with updated qty
    const variant = this.getSelectedVariant(card);
    if (variant) {
      this.updateCardVariant(card, variant);
    } else {
      this.updateTotalPrice();
    }
  }

  toggleOptional(card, checked) {
    const hiddenInput = card.querySelector('.kit-component__input');
    const picker = card.querySelector('[data-kit-picker]');

    if (hiddenInput) {
      hiddenInput.checked = checked;
    }

    card.classList.toggle('kit-component--selected', checked);

    // Sync drawer select button state
    const drawerSelectBtn = card.querySelector('[data-kit-drawer-select]');
    if (drawerSelectBtn) {
      const btnText = drawerSelectBtn.querySelector('.btn-text');
      if (checked) {
        btnText.textContent = 'Selected';
        drawerSelectBtn.disabled = true;
      } else {
        btnText.textContent = 'Select';
        drawerSelectBtn.disabled = false;
      }
    }

    // Grid mode: open picker drawer on selection
    if (card.dataset.displayLayout === 'grid') {
      if (checked) {
        const trigger = card.querySelector('[data-kit-picker-trigger]');
        if (trigger) {
          setTimeout(() => trigger.click(), 50);
        }
      }
    } else {
      // List mode: toggle inline picker visibility
      if (picker) {
        picker.classList.toggle('kit-component__picker--collapsed', !checked);
      }
      const qtyStepper = card.querySelector('[data-kit-qty-stepper]');
      if (qtyStepper) {
        qtyStepper.classList.toggle('kit-component__picker--collapsed', !checked);
      }
    }

    this.updateTotalPrice();
    this.updateSubmitState();
  }

  updateTotalPrice() {
    if (!this.buyPriceEl) return;

    // Only show kit components total (parent product is not added to cart)
    let kitTotal = 0;
    const checkedInputs = this.root.querySelectorAll('.kit-component__input:checked:not(:disabled)');
    checkedInputs.forEach(input => {
      const price = parseInt(input.dataset.price || '0', 10);
      const baseQty = parseInt(input.dataset.qty || '1', 10) || 1;
      const multiplier = parseInt(input.dataset.qtyMultiplier || '1', 10) || 1;
      kitTotal += price * baseQty * multiplier;
    });

    const qty = this.getQuantity();
    const total = kitTotal * qty;
    this.buyPriceEl.innerHTML = this.formatMoney(total);
  }

  getQuantity() {
    const qtyInput = (this.productInfo || document).querySelector('quantity-input input[name="quantity"]');
    return parseInt(qtyInput?.value || '1', 10) || 1;
  }

  parseMoney(str) {
    if (!str) return 0;
    const cleaned = str.replace(/[^0-9.,]/g, '').replace(',', '');
    return Math.round(parseFloat(cleaned) * 100) || 0;
  }

  formatMoney(cents) {
    if (typeof theme !== 'undefined' && theme.Currency?.formatMoney) {
      const format = theme.settings?.currencyCodeEnabled
        ? theme.settings.moneyWithCurrencyFormat
        : theme.settings.moneyFormat;
      return theme.Currency.formatMoney(cents, format);
    }
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  updateGroupBtnText(toggleBtn, isPressed) {
    if (toggleBtn.dataset.selectText) {
      toggleBtn.textContent = isPressed ? toggleBtn.dataset.selectedText : toggleBtn.dataset.selectText;
    }
  }

  getFirstUnsatisfiedGroupWarning() {
    for (const group of this.groups) {
      const mode = group.dataset.selectionMode;
      if (mode === 'optional') continue;

      const inputs = group.querySelectorAll('.kit-component__input');
      const checkedCount = Array.from(inputs).filter(i => i.checked).length;

      if (mode === 'one_required' && checkedCount !== 1) {
        return group.dataset.selectionWarning || 'Please select from the required options';
      }
      if (mode === 'one_or_more_required' && checkedCount < 1) {
        return group.dataset.selectionWarning || 'Please select from the required options';
      }
    }
    return null;
  }

  areGroupsSatisfied() {
    return this.groups.every(group => {
      const mode = group.dataset.selectionMode;
      if (mode === 'optional') return true;

      const inputs = group.querySelectorAll('.kit-component__input');
      const checkedCount = Array.from(inputs).filter(i => i.checked).length;

      if (mode === 'one_required') return checkedCount === 1;
      if (mode === 'one_or_more_required') return checkedCount >= 1;
      return true;
    });
  }

  updateSubmitState() {
    const requiredCards = this.cards.filter(c => c.dataset.required === 'true');
    const allRequiredSatisfied = requiredCards.every(card => {
      const input = card.querySelector('.kit-component__input');
      return input && input.checked && !input.disabled;
    });

    const allGroupsSatisfied = this.areGroupsSatisfied();
    const canSubmit = allRequiredSatisfied && allGroupsSatisfied;

    if (!canSubmit && this.submitLabel) {
      const warningText = this.getFirstUnsatisfiedGroupWarning() || this.requiredButtonText || '';
      if (warningText) {
        this.submitLabel.textContent = warningText;
      }
      this.submitButton?.setAttribute('aria-disabled', 'true');
    } else if (canSubmit && this.submitButton?.hasAttribute('aria-disabled') && this.originalSubmitText) {
      this.submitLabel.textContent = this.originalSubmitText;
      this.submitButton.removeAttribute('aria-disabled');
    }
  }

  attachFormGuard() {
    if (!this.form) return;

    this.form.addEventListener('submit', (event) => {
      // Check required standalone cards
      const requiredCards = this.cards.filter(c => c.dataset.required === 'true');
      const unsatisfiedCards = requiredCards.filter(card => {
        const input = card.querySelector('.kit-component__input');
        return !input || !input.checked || input.disabled;
      });

      // Check required groups
      const unsatisfiedGroups = this.groups.filter(group => {
        const mode = group.dataset.selectionMode;
        if (mode === 'optional') return false;
        const inputs = group.querySelectorAll('.kit-component__input');
        const checkedCount = Array.from(inputs).filter(i => i.checked).length;
        if (mode === 'one_required') return checkedCount !== 1;
        if (mode === 'one_or_more_required') return checkedCount < 1;
        return false;
      });

      if (unsatisfiedCards.length > 0 || unsatisfiedGroups.length > 0) {
        event.preventDefault();
        event.stopImmediatePropagation();

        // Highlight unsatisfied standalone cards
        unsatisfiedCards.forEach(card => card.classList.add('kit-component--error'));
        setTimeout(() => unsatisfiedCards.forEach(card => card.classList.remove('kit-component--error')), 3000);

        // Highlight unsatisfied group cards
        unsatisfiedGroups.forEach(group => {
          const groupCards = group.querySelectorAll('[data-kit-item]');
          groupCards.forEach(card => card.classList.add('kit-component--error'));
          setTimeout(() => groupCards.forEach(card => card.classList.remove('kit-component--error')), 3000);
        });

        // Show error message
        let errorText = '';
        if (unsatisfiedGroups.length > 0) {
          errorText = unsatisfiedGroups[0].dataset.selectionWarning || 'Please select from the required options';
        } else {
          errorText = this.requiredButtonText || 'Please select all required kit options';
        }

        if (this.errorMessage) {
          this.errorMessage.removeAttribute('hidden');
          this.errorMessage.textContent = errorText;
          setTimeout(() => {
            this.errorMessage.setAttribute('hidden', '');
          }, 5000);
        }
      }
    }, true); // capture phase to run before ProductForm handler
  }

  updateConditionalVisibility() {
    const requiredVariantAttr = this.root.dataset.requiredVariant;
    if (!requiredVariantAttr) return;

    const requiredIds = requiredVariantAttr.split(',').map(s => s.trim());
    const mainForm = this.form || document.querySelector('form[action$="/cart/add"]');
    const currentVariantId = mainForm?.querySelector('[name="id"]')?.value || '';

    if (requiredIds.includes(currentVariantId)) {
      this.root.removeAttribute('hidden');
      this.root.removeAttribute('aria-hidden');
    } else {
      this.root.setAttribute('hidden', '');
      this.root.setAttribute('aria-hidden', 'true');

      // Uncheck all kit items when hidden
      this.root.querySelectorAll('.kit-component__input:checked').forEach(input => {
        input.checked = false;
      });
    }
  }
}

// Initialize all kit builders on page
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-kit-builder="true"]').forEach(root => {
    new KitBuilder(root);
  });
});

// Re-initialize on Shopify section/block changes (theme editor)
if (Shopify.designMode) {
  const reinitKit = (event) => {
    const section = event.target.closest('.shopify-section') || event.target;
    section.querySelectorAll('[data-kit-builder="true"]').forEach(root => {
      new KitBuilder(root);
    });
  };
  document.addEventListener('shopify:section:load', reinitKit);
  document.addEventListener('shopify:block:select', reinitKit);
  document.addEventListener('shopify:block:deselect', reinitKit);
}
